import { Entity } from '@data/models/entity.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@app/services/error.service';
import {
    Authorization,
    CommTypes,
    communicationConfiguration,
    embed,
    EmbedUserCommunication,
    EmbedUserRole,
    ProjectCommunication,
    ProjectRole,
    UserCommunication,
    UserRole,
    userRoleConfiguration,
} from '@data/models/authorization.model';
import { Embedded } from '@data/models/embed.model';
import { User } from '@data/models/user.model';
import { toParams } from '@shared/utils/query-to-params';
import {
    cloneDeep,
    concat,
    uniq,
} from 'lodash';
import { Observable } from 'rxjs';
import {
    catchError,
    map,
} from 'rxjs/operators';

export const AUTH_SERVICE_USERS_API_ROOT = '/auth/v1/users';
export const AUTH_SERVICE_PROJECTS_API_ROOT = '/auth/v1/projects';
export const AUTH_SERVICE_ENTITIES_API_ROOT = '/auth/v1/entities';

@Injectable()
export class AuthorizationService {

    public commTypes: CommTypes[] = ['MSO', 'INCIDENT', 'REQUEST', 'PATCH', 'CHANGE', 'BILLING'];

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient
    ) {
        this.errors = concat(this.errorService.projectServiceErrors, this.errorService.userServiceErrors);
    }

    public ngInit(): void {
    }

    /**
     * Get list of roles for a single user
     */
    public getRolesByUser<T extends embed>(userId: string, embedFields: T[] = []): Observable<EmbedUserRole<T>[]> {
        const embeds = uniq(embedFields.map((it) => userRoleConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });

        return this.http.get<{ userRoles: UserRole[]; embedded: Embedded }>(AUTH_SERVICE_USERS_API_ROOT + '/' + userId + '/roles', { params }).pipe(
            map(({ userRoles, embedded }) => UserRole.toCustomUserRoleList(userRoles, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedUserRole<T>[]>(this.errors, 'Récupération des droits d\'un utilisateur', []))
        );
    }

    /**
     * Get list of roles for a single project
     */
    public getRolesByProject(projectId: string): Observable<ProjectRole[]> {
        return this.http.get<{ projectRoles: ProjectRole[] }>(AUTH_SERVICE_PROJECTS_API_ROOT + '/' + projectId + '/roles').pipe(
            map((result) => result.projectRoles),
            catchError(this.errorService.errorHandler<ProjectRole[]>(this.errors, 'Récupération des droits projet', []))
        );
    }

    /**
     * Get list of communications for a single user
     */
    public getCommunicationsByUser<T extends embed>(userId: string, embedFields: T[] = []): Observable<EmbedUserCommunication<T>[]> {
        const embeds = uniq(embedFields.map((it) => communicationConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });

        return this.http.get<{ communications: UserCommunication[]; embedded: Embedded }>(AUTH_SERVICE_USERS_API_ROOT + '/' + userId + '/communications', { params })
            .pipe(
                map(({ communications, embedded }) => UserCommunication.toCustomUserCommunicationList(communications, embedded, embedFields)),
                catchError(this.errorService.errorHandler<EmbedUserCommunication<T>[]>(this.errors, 'Récupération des communications d\'un utilisateur', []))
            );
    }

    /**
     * Get list of communications for a single project
     */
    public getCommunicationsByProject(projectId: string): Observable<ProjectCommunication[]> {
        return this.http.get<{ communications: ProjectCommunication[] }>(AUTH_SERVICE_PROJECTS_API_ROOT + '/' + projectId + '/communications')
            .pipe(
                map((result) => result.communications),
                catchError(this.errorService.errorHandler<ProjectCommunication[]>(
                    this.errors,
                    'Récupération des communication du projet',
                    []
                ))
            );
    }

    /**
     * Get list of project roles for current user
     */
    public getMyAuthorizations(): Observable<UserRole[]> {
        return this.http.get<any>(AUTH_SERVICE_USERS_API_ROOT + '/me/authorizations').pipe(
            map((result) => result.authorizations),
            catchError(this.errorService.errorHandler<UserRole[]>(this.errors, 'Récupération de mes autorisations', []))
        );
    }

    /**
     * Get list of communications for current user
     */
    public getMyCommunications(): Observable<UserCommunication[]> {
        return this.http.get<any>(AUTH_SERVICE_USERS_API_ROOT + '/me/communications').pipe(
            map((result) => result.communications),
            catchError(this.errorService.errorHandler<UserCommunication[]>(this.errors, 'Récupération de mes communications', []))
        );
    }

    /**
     * Get list of roles for current user
     */
    public getMyRoles(): Observable<UserRole[]> {
        return this.http.get<{ userRoles: UserRole[] }>(AUTH_SERVICE_USERS_API_ROOT + '/me/roles').pipe(
            map((result) => result.userRoles),
            catchError(this.errorService.errorHandler<UserRole[]>(this.errors, 'Récupération des mes droits', []))
        );
    }

    /**
     * Update user communications
     */
    public updateCommunicationsByUser(userId: string, communications: UserCommunication[]): Observable<UserCommunication[]> {
        return this.http.patch<{ communications: UserCommunication[] }>(AUTH_SERVICE_USERS_API_ROOT + '/' + userId + '/communications', communications)
            .pipe(
                map((result) => result.communications),
                catchError(this.errorService.errorHandler<UserCommunication[]>(
                    this.errors,
                    'Modification des communication d\'un utilisateur',
                    []
                ))
            );
    }

    /**
     * Update project communications
     */
    public updateCommunicationsByProject(projectId: string, body: ProjectCommunication[], options?: { shouldThrowError?: boolean }): Observable<ProjectCommunication[]> {
        return this.http.patch<ProjectCommunication[]>(AUTH_SERVICE_PROJECTS_API_ROOT + '/' + projectId + '/communications', body)
            .pipe(
                catchError(this.errorService.errorHandler<ProjectCommunication[]>(this.errors, 'Modification des communications', [], options?.shouldThrowError))
            );
    }

    /**
     * Update user roles
     */
    public updateRolesByUser(userId: string, roles: UserRole[]): Observable<UserRole[]> {
        return this.http.patch<{ userRoles: UserRole[] }>(AUTH_SERVICE_USERS_API_ROOT + '/' + userId + '/roles', roles)
            .pipe(
                map((result) => result.userRoles),
                catchError(this.errorService.errorHandler<UserRole[]>(this.errors, 'Modification des droits d\'un utilisateur', []))
            );
    }

    /**
     * Update project roles
     */
    public updateRolesByProject(projectId: string, body: ProjectRole[], options?: { shouldThrowError?: boolean }): Observable<ProjectRole[]> {
        return this.http.patch<{ projectRoles: ProjectRole[] }>(AUTH_SERVICE_PROJECTS_API_ROOT + '/' + projectId + '/roles', body)
            .pipe(
                map((result) => result.projectRoles),
                catchError(this.errorService.errorHandler<ProjectRole[]>(this.errors, 'Modification des droits d\'un projet', [], options?.shouldThrowError))
            );
    }

    /**
     * Get the fusion of user roles and communications
     */
    public getUserAuthorizations(userRoles: EmbedUserRole<'$project'>[], userComm: UserCommunication[]): Authorization[] {
        return userRoles.map((userRole) => {
            const authorization: Authorization = cloneDeep(userRole);
            const userCommunication = userComm.find((communication) => communication.projectId === userRole.projectId);
            if (userCommunication) {
                authorization.communicationTypes = userCommunication.communicationTypes;
            }
            return authorization;
        });
    }

    /**
     * Get the fusion of project roles and communications
     */
    public getProjectAuthorizations(projectRoles: ProjectRole[], projectComm: ProjectCommunication[], users?: User[]): Authorization[] {
        return projectRoles.map((role) => {
            const authorization: Authorization = cloneDeep(role);
            const projectCommunication = projectComm.find((communication) => communication.userId === role.userId);
            if (projectCommunication) {
                authorization.communicationTypes = projectCommunication.communicationTypes;
            }

            if (users && users[role.userId]) {
                authorization.user = users[role.userId];
                authorization.userInfos = users[role.userId].firstName + ' ' + users[role.userId].lastName;
            }
            return authorization;
        });
    }

    public findEntities(): Observable<Entity[]> {
        return this.http.get<{ entities: Entity[] }>(AUTH_SERVICE_ENTITIES_API_ROOT).pipe(
            map((result) => result.entities),
            catchError(this.errorService.errorHandler<Entity[]>(this.errors, 'Récupération des entités', []))
        );
    }
}

